'use client';

import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useCart } from '@kidslabs/module-quote';
import { useTranslation } from 'next-i18next';
import { useAvailableVouchers, useMarketingAppliedCodes } from '../../../api';
import type { StorefrontAllocation, AppliedVoucher } from '../../../types/marketing.graphql';
import type { CouponSectionType } from '../components/coupon-list/coupon-list.types';
import { useCouponContext } from '../contexts/coupon.context';
import type { UnifiedSalesRule } from '../types/sales-rule.types';
import { evaluateVoucherConditions, type ConditionEvaluationResult, type MagentoCondition } from '../utils';
import { useVoucherEvaluationContext } from './use-voucher-evaluation-context';

export interface VoucherSectionItem {
    allocation: StorefrontAllocation;
    isApplied: boolean;
    conditionResult?: ConditionEvaluationResult;
    applicableAmount?: number;
    ineligibilityReason?: string;
}

export interface VoucherSectionType {
    title: string;
    data: VoucherSectionItem[];
    isDisabled: boolean;
}

/**
 * Map a StorefrontAllocation to UnifiedSalesRule shape so it can be rendered
 * by the same CouponItem (radio button) UI used for coupons.
 *
 * The `id` field distinguishes vouchers from coupons and stores the allocation ID.
 * CouponItem displays `name` as the title when `id` is present (voucher),
 * and falls back to `code` for regular coupons.
 */
export const mapAllocationToSalesRule = (
    allocation: StorefrontAllocation,
    conditionResult?: ConditionEvaluationResult,
    ineligibilityReason?: string
): UnifiedSalesRule => ({
    __typename: 'SalesRule',
    id: allocation.id,
    type: 'VOUCHER',
    code: allocation.name,
    name: allocation.name,
    description: allocation.description ?? undefined,
    from_date: allocation.start_at ?? undefined,
    to_date: allocation.expires_at ?? undefined,
    is_active: true,
    conditionResult,
    ineligibilityReason,
    applicableAmount: allocation.sample_amount ?? undefined,
});

export const isVoucherSalesRule = (salesRule?: UnifiedSalesRule): boolean => {
    return salesRule?.type === 'VOUCHER';
};

export const useVoucherList = () => {
    const { t } = useTranslation('flash-sale');
    const { cartId } = useCart();
    const couponContext = useCouponContext();
    const evaluationContext = useVoucherEvaluationContext();
    // Only auto-sync applied voucher to selection on initial data load.
    // Once synced, stop so user's manual deselection is not overridden.
    const hasAutoSyncedRef = useRef(false);

    const {
        availableVouchers,
        loading: loadingAvailableVouchers,
        error: availableVouchersError,
        refetch: refetchAvailableVouchers,
    } = useAvailableVouchers({
        cart_id: cartId,
        location: couponContext?.displayLocation,
    });

    const {
        appliedVouchers,
        loading: loadingAppliedVouchers,
        refetch: refetchAppliedCodes,
    } = useMarketingAppliedCodes({ cartId });

    const selectedCouponCode = couponContext?.couponState$?.selectedCoupon?.get()?.code;

    const appliedVoucherIds = useMemo(
        () => new Set(appliedVouchers.map((v: AppliedVoucher) => v.voucher_id)),
        [appliedVouchers]
    );

    const mergedAllocations = useMemo(() => {
        const appliedAllocation = appliedVouchers[0]?.allocation;

        if (!appliedAllocation) {
            return availableVouchers;
        }

        return availableVouchers.some(allocation => allocation.id === appliedAllocation.id)
            ? availableVouchers
            : [appliedAllocation, ...availableVouchers];
    }, [appliedVouchers, availableVouchers]);

    const voucherItems: VoucherSectionItem[] = useMemo(
        () =>
            mergedAllocations.map((allocation: StorefrontAllocation) => ({
                allocation,
                isApplied: appliedVoucherIds.has(allocation.id),
            })),
        [mergedAllocations, appliedVoucherIds]
    );

    const { availableItems, unavailableItems } = useMemo(() => {
        const available: VoucherSectionItem[] = [];
        const unavailable: VoucherSectionItem[] = [];

        for (const item of voucherItems) {
            const hasQuota =
                (item.allocation.remaining_user_quota === null ||
                    item.allocation.remaining_user_quota === undefined ||
                    item.allocation.remaining_user_quota > 0) &&
                (item.allocation.remaining_allocation_quota === null ||
                    item.allocation.remaining_allocation_quota === undefined ||
                    item.allocation.remaining_allocation_quota > 0);

            const isExpired = item.allocation.expires_at ? new Date(item.allocation.expires_at) < new Date() : false;
            const isNotYetStarted = item.allocation.start_at ? new Date(item.allocation.start_at) > new Date() : false;

            // Evaluate magento_conditions against current cart + customer context
            const conditionResult = evaluateVoucherConditions(
                item.allocation.magento_conditions as MagentoCondition | null,
                evaluationContext
            );

            // Determine human-readable ineligibility reason
            let ineligibilityReason: string | undefined;
            if (!hasQuota) {
                ineligibilityReason = t('text_voucher_quota_exhausted', { defaultValue: 'Het luot su dung' });
            } else if (isExpired) {
                ineligibilityReason = t('text_voucher_expired', { defaultValue: 'Da het han' });
            } else if (isNotYetStarted) {
                ineligibilityReason = t('text_voucher_not_yet_started', { defaultValue: 'Chua den thoi gian su dung' });
            } else if (!conditionResult.isEligible && conditionResult.unmetConditions.length > 0) {
                ineligibilityReason = conditionResult.unmetConditions[0].message;
            }

            if (hasQuota && !isExpired && !isNotYetStarted && conditionResult.isEligible) {
                available.push({
                    ...item,
                    conditionResult,
                    applicableAmount: item.allocation.sample_amount ?? undefined,
                });
            } else {
                unavailable.push({
                    ...item,
                    conditionResult,
                    ineligibilityReason,
                });
            }
        }

        return { availableItems: available, unavailableItems: unavailable };
    }, [voucherItems, evaluationContext, t]);

    const voucherSections: VoucherSectionType[] = useMemo(
        () => [
            {
                isDisabled: false,
                title: t('text_voucher_available', {
                    count: availableItems.length,
                    defaultValue: `Voucher khả dụng ({{count}})`,
                }),
                data: availableItems,
            },
            ...(unavailableItems.length > 0
                ? [
                      {
                          isDisabled: true,
                          title: t('text_voucher_unavailable', {
                              count: unavailableItems.length,
                              defaultValue: `Voucher không khả dụng ({{count}})`,
                          }),
                          data: unavailableItems,
                      },
                  ]
                : []),
        ],
        [availableItems, unavailableItems, t]
    );

    /**
     * Voucher sections mapped to CouponSectionType so they can be rendered
     * by the same CouponSection / CouponItem (radio button) UI as coupons.
     */
    const voucherCouponSections: CouponSectionType[] = useMemo(
        () => [
            {
                isDisabled: false,
                title: t('text_voucher_available', {
                    count: availableItems.length,
                    defaultValue: 'Voucher kha dung ({{count}})',
                }),
                data: availableItems.map(item =>
                    mapAllocationToSalesRule(item.allocation, item.conditionResult)
                ) as CouponSectionType['data'],
            },
            ...(unavailableItems.length > 0
                ? [
                      {
                          isDisabled: true,
                          title: t('text_voucher_unavailable', {
                              count: unavailableItems.length,
                              defaultValue: 'Voucher khong kha dung ({{count}})',
                          }),
                          data: unavailableItems.map(item =>
                              mapAllocationToSalesRule(item.allocation, item.conditionResult, item.ineligibilityReason)
                          ) as CouponSectionType['data'],
                      },
                  ]
                : []),
        ],
        [availableItems, unavailableItems, t]
    );

    /** Map from allocation ID → applied voucher ID for removal purposes */
    const allocationToVoucherIdMap = useMemo(() => {
        const map = new Map<string, string>();
        for (const v of appliedVouchers) {
            map.set(v.voucher_id, v.voucher_id);
        }
        return map;
    }, [appliedVouchers]);

    // Reset sync flag when applied vouchers change from server
    const prevAppliedVoucherIdRef = useRef(appliedVouchers?.[0]?.voucher_id);
    useEffect(() => {
        const currentId = appliedVouchers?.[0]?.voucher_id;
        if (prevAppliedVoucherIdRef.current !== currentId) {
            prevAppliedVoucherIdRef.current = currentId;
            hasAutoSyncedRef.current = false;
        }
    }, [appliedVouchers]);

    useEffect(() => {
        // Only auto-sync on initial data load. Once synced (or if user has
        // interacted via useCouponList), stop to avoid overriding deselection.
        if (hasAutoSyncedRef.current) return;

        if (mergedAllocations && mergedAllocations.length > 0) {
            const matchedAllocation = mergedAllocations.find(
                item => item?.name === appliedVouchers[0]?.allocation_name
            );

            if (!matchedAllocation) {
                return;
            }

            hasAutoSyncedRef.current = true;

            const matchedVoucher = mapAllocationToSalesRule(matchedAllocation);
            couponContext?.couponState$?.selectedVoucherInfo.set({
                allocationId: matchedAllocation.id,
                voucherId: appliedVouchers[0]?.voucher_id,
            });

            if (matchedVoucher && selectedCouponCode !== matchedVoucher?.code) {
                couponContext?.couponState$?.selectedCoupon.set(matchedVoucher);
            }
        }
    }, [appliedVouchers, availableVouchers, couponContext, mergedAllocations, selectedCouponCode]);

    const handleRefresh = useCallback(() => {
        refetchAvailableVouchers();
        refetchAppliedCodes();
    }, [refetchAvailableVouchers, refetchAppliedCodes]);

    return {
        voucherSections,
        voucherCouponSections,
        appliedVouchers,
        availableVouchers,
        allocationToVoucherIdMap,
        loading: loadingAvailableVouchers || loadingAppliedVouchers,
        error: availableVouchersError,
        onRefresh: handleRefresh,
        availableItems,
        unavailableItems,
    };
};
